import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useVisibleRefresh } from "@/hooks/useVisibleRefresh";
import { toast } from "sonner";
import {
  Archive,
  Check,
  Clock3,
  Copy,
  ExternalLink,
  GraduationCap,
  KeyRound,
  Loader2,
  Mail,
  MoreHorizontal,
  Pencil,
  Plus,
  Power,
  Search,
  ShieldCheck,
  Trash2,
  UserCheck,
  UserCog,
  UserRound,
  UserRoundX,
  UsersRound,
  X,
} from "lucide-react";
import { brDateTime, tr, ROLE_LABEL, STUDENT_STATUS_LABEL } from "@/lib/labels";
import DoxaSelect from "@/components/doxa/DoxaSelect";
import { EmptyState, PageHeader, SectionCard, StatCard } from "@/components/doxa/AdminSurface";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

const INPUT_CLASS = "w-full rounded-xl border border-[#E0D9CD] bg-white px-3 py-2.5 text-sm text-[#1F1F1F] outline-none transition placeholder:text-[#A59C90] hover:border-[#CFC5B7] focus:border-[#F75A1B] focus:ring-2 focus:ring-[#F75A1B]/15 disabled:cursor-not-allowed disabled:bg-[#F6F3EE] disabled:text-[#9A9287]";

const ROLE_META = {
  MASTER: {
    label: "Master",
    badge: "doxa-badge-orange",
    description: "Controle total do sistema e das configurações de governança.",
  },
  ADMIN: {
    label: "Admin",
    badge: "doxa-badge-warning",
    description: "Segue as permissões configuradas para o perfil Admin.",
  },
  PROFESSOR: {
    label: "Professor",
    badge: "doxa-badge-success",
    description: "Segue as permissões configuradas para o perfil Professor.",
  },
  STUDENT: {
    label: "Aluno",
    badge: "doxa-badge-neutral",
    description: "Conta do Portal do Aluno. A vida acadêmica é gerida em Alunos.",
  },
};

const ACCESS_META = {
  ACTIVE: {
    label: "Acesso ativo",
    short: "Ativo",
    badge: "doxa-badge-success",
    description: "Pode entrar no sistema normalmente.",
  },
  INACTIVE: {
    label: "Acesso inativo",
    short: "Inativo",
    badge: "doxa-badge-neutral",
    description: "Não consegue entrar até ser reativado.",
  },
  FIRST_ACCESS_PENDING: {
    label: "Aguardando 1º acesso",
    short: "1º acesso",
    badge: "doxa-badge-warning",
    description: "Ainda precisa abrir o convite e definir a própria senha.",
  },
  RESET_PENDING: {
    label: "Redefinição pendente",
    short: "Redefinição",
    badge: "doxa-badge-warning",
    description: "Um novo primeiro acesso foi gerado e ainda não foi concluído.",
  },
  REGISTRATION_PENDING: {
    label: "Aguardando aprovação",
    short: "Aprovação",
    badge: "doxa-badge-warning",
    description: "A inscrição do aluno ainda precisa ser analisada em Inscrições.",
  },
  BLOCKED: {
    label: "Conta bloqueada",
    short: "Bloqueado",
    badge: "doxa-badge-critical",
    description: "Estado protegido. Exige tratamento específico antes de qualquer mudança.",
  },
  REMOVED: {
    label: "Removido da operação",
    short: "Removido",
    badge: "doxa-badge-critical",
    description: "Não acessa o sistema; IDs e histórico permanecem preservados.",
  },
  PENDING: {
    label: "Acesso pendente",
    short: "Pendente",
    badge: "doxa-badge-warning",
    description: "Há uma etapa de acesso pendente.",
  },
};

const ACADEMIC_BADGE = {
  ACTIVE: "doxa-badge-success",
  PENDING: "doxa-badge-warning",
  INACTIVE: "doxa-badge-neutral",
  DROPPED: "doxa-badge-neutral",
  COMPLETED: "doxa-badge-orange",
  REJECTED: "doxa-badge-critical",
};

const TAB_OPTIONS = [
  { value: "staff", label: "Equipe", icon: UsersRound },
  { value: "students", label: "Alunos", icon: GraduationCap },
  { value: "removed", label: "Removidos", icon: Archive },
];

const ACCESS_FILTER_OPTIONS = [
  { value: "ALL", label: "Todo acesso" },
  { value: "ACTIVE", label: "Acesso ativo" },
  { value: "PENDING", label: "Acesso pendente" },
  { value: "INACTIVE", label: "Acesso inativo" },
  { value: "BLOCKED", label: "Bloqueado" },
  { value: "NEVER_LOGGED", label: "Nunca acessou" },
];

const SORT_OPTIONS = [
  { value: "NAME", label: "Nome A–Z" },
  { value: "LAST_LOGIN", label: "Último acesso" },
  { value: "NEWEST", label: "Criação mais recente" },
];

function initials(name = "") {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "?";
}

function accessMeta(user) {
  return ACCESS_META[user?.access_context] || ACCESS_META[user?.access_status] || {
    label: user?.access_status || "Estado desconhecido",
    short: user?.access_status || "—",
    badge: "doxa-badge-neutral",
    description: "Estado de acesso ainda não mapeado para a interface.",
  };
}

function relativeLogin(value) {
  if (!value) return "Nunca acessou";
  const date = new Date(value);
  const diff = Date.now() - date.getTime();
  if (!Number.isFinite(diff)) return brDateTime(value);
  const minutes = Math.max(0, Math.floor(diff / 60000));
  if (minutes < 1) return "Agora";
  if (minutes < 60) return `Há ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Há ${hours}h`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "Ontem";
  if (days < 30) return `Há ${days} dias`;
  return brDateTime(value);
}

function RoleBadge({ role }) {
  const meta = ROLE_META[role] || { label: tr(ROLE_LABEL, role), badge: "doxa-badge-neutral" };
  return <span className={`doxa-badge ${meta.badge}`}>{meta.label}</span>;
}

function AccessBadge({ user, withDescription = false }) {
  const meta = accessMeta(user);
  return (
    <div className="min-w-0">
      <span className={`doxa-badge ${meta.badge}`}>{meta.label}</span>
      {withDescription && <p className="mt-1 text-[11px] leading-relaxed text-[#8A8378]">{meta.description}</p>}
    </div>
  );
}

function AccountAvatar({ user, size = "md" }) {
  const cls = size === "lg" ? "h-14 w-14 text-base rounded-2xl" : "h-9 w-9 text-xs rounded-xl";
  return (
    <span className={`grid shrink-0 place-items-center bg-[#FFF0E9] font-bold text-[#D94A12] ${cls}`} aria-hidden="true">
      {initials(user?.name)}
    </span>
  );
}

function AccountActions({ account, onDetails, onEdit, onReset, onToggle, onRemove, onOpenStudent, onOpenRegistration, mobile = false }) {
  const caps = account.capabilities || {};
  const pendingAccess = ["FIRST_ACCESS_PENDING", "RESET_PENDING"].includes(account.access_context);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={`grid place-items-center rounded-xl border border-[#E0D9CD] bg-white text-[#6B655E] transition hover:border-[#F75A1B]/40 hover:bg-[#FFF7F2] hover:text-[#D94A12] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F75A1B]/25 ${mobile ? "h-9 w-9" : "h-8 w-8"}`}
          aria-label={`Ações de ${account.name}`}
          data-testid={`user-actions-${account.id}`}
        >
          <MoreHorizontal className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 rounded-xl border-[#E0D9CD] p-1.5">
        <DropdownMenuLabel className="text-xs text-[#8A8378]">Conta e acesso</DropdownMenuLabel>
        <DropdownMenuItem onSelect={() => onDetails(account)} className="rounded-lg">
          <UserRound className="h-4 w-4" /> Ver detalhes
        </DropdownMenuItem>
        {caps.edit && (
          <DropdownMenuItem onSelect={() => onEdit(account)} className="rounded-lg" data-testid={`edit-${account.id}`}>
            <Pencil className="h-4 w-4" /> Editar usuário
          </DropdownMenuItem>
        )}
        {caps.reset_access && (
          <DropdownMenuItem onSelect={() => onReset(account)} className="rounded-lg" data-testid={`reset-${account.id}`}>
            <KeyRound className="h-4 w-4" /> {pendingAccess ? "Gerar novo link" : "Redefinir acesso"}
          </DropdownMenuItem>
        )}
        {caps.toggle_access && (
          <DropdownMenuItem onSelect={() => onToggle(account)} className="rounded-lg" data-testid={`toggle-${account.id}`}>
            <Power className="h-4 w-4" /> {account.access_status === "ACTIVE" ? "Inativar acesso" : "Reativar acesso"}
          </DropdownMenuItem>
        )}
        {account.student && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => onOpenStudent(account)} className="rounded-lg">
              <GraduationCap className="h-4 w-4" /> Abrir perfil acadêmico
            </DropdownMenuItem>
          </>
        )}
        {account.access_context === "REGISTRATION_PENDING" && (
          <DropdownMenuItem onSelect={() => onOpenRegistration(account)} className="rounded-lg">
            <ExternalLink className="h-4 w-4" /> Abrir em Inscrições
          </DropdownMenuItem>
        )}
        {caps.remove && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => onRemove(account)} className="rounded-lg text-[#C53030] focus:text-[#C53030]" data-testid={`remove-${account.id}`}>
              <Trash2 className="h-4 w-4" /> Remover da operação
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Users() {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("staff");
  const [roleFilter, setRoleFilter] = useState("ALL");
  const [accessFilter, setAccessFilter] = useState("ALL");
  const [sortBy, setSortBy] = useState("NAME");
  const [showCreate, setShowCreate] = useState(false);
  const [detailsId, setDetailsId] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [toggleTarget, setToggleTarget] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [inviteResult, setInviteResult] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);

  const load = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/users");
      setUsers(data);
      return data;
    } catch (err) {
      if (!silent) toast.error(formatApiError(err));
      return [];
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);
  useVisibleRefresh(() => load({ silent: true }), { intervalMs: 0 });

  const selectedAccount = detailsId ? users.find((item) => item.id === detailsId) || null : null;
  const staffUsers = users.filter((item) => item.role !== "STUDENT" && item.access_status !== "REMOVED");
  const studentUsers = users.filter((item) => item.role === "STUDENT" && item.access_status !== "REMOVED");
  const removedUsers = users.filter((item) => item.access_status === "REMOVED");

  const stats = {
    staffActive: staffUsers.filter((item) => item.access_status === "ACTIVE").length,
    pendingAccess: staffUsers.filter((item) => item.access_status === "PENDING").length,
    inactive: staffUsers.filter((item) => item.access_status === "INACTIVE").length,
    removed: removedUsers.length,
  };

  const filtered = useMemo(() => {
    const source = tab === "staff" ? staffUsers : tab === "students" ? studentUsers : removedUsers;
    const query = q.trim().toLowerCase();
    const rows = source.filter((item) => {
      const meta = accessMeta(item);
      const haystack = [
        item.name,
        item.email,
        tr(ROLE_LABEL, item.role),
        meta.label,
        item.student?.class_group_name,
        item.student?.full_name,
        tr(STUDENT_STATUS_LABEL, item.student?.status),
      ].filter(Boolean).join(" ").toLowerCase();
      if (query && !haystack.includes(query)) return false;
      if (roleFilter !== "ALL" && item.role !== roleFilter) return false;
      if (accessFilter === "NEVER_LOGGED" && item.last_login_at) return false;
      if (accessFilter !== "ALL" && accessFilter !== "NEVER_LOGGED" && item.access_status !== accessFilter) return false;
      return true;
    });

    return rows.sort((a, b) => {
      if (sortBy === "NEWEST") return String(b.created_at || "").localeCompare(String(a.created_at || ""));
      if (sortBy === "LAST_LOGIN") return String(b.last_login_at || "").localeCompare(String(a.last_login_at || ""));
      return String(a.name || "").localeCompare(String(b.name || ""), "pt-BR", { sensitivity: "base" });
    });
  }, [users, tab, q, roleFilter, accessFilter, sortBy]);

  const switchTab = (next) => {
    setTab(next);
    setRoleFilter("ALL");
    setAccessFilter("ALL");
    setQ("");
  };

  const clearFilters = () => {
    setQ("");
    setRoleFilter("ALL");
    setAccessFilter("ALL");
    setSortBy("NAME");
  };

  const openStudent = (account) => {
    if (account.student?.id) navigate(`/students/${account.student.id}`);
  };

  const openRegistration = () => navigate("/registrations?tab=pending");

  const confirmReset = async () => {
    if (!resetTarget) return;
    setActionBusy(true);
    try {
      const { data } = await api.post(`/users/${resetTarget.id}/reset-access`);
      const url = `${window.location.origin}/first-access/${data.invite_token}`;
      const targetName = resetTarget.name;
      setResetTarget(null);
      await load({ silent: true });
      setInviteResult({ kind: "reset", name: targetName, url });
      toast.success("Novo primeiro acesso gerado.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setActionBusy(false);
    }
  };

  const confirmToggle = async () => {
    if (!toggleTarget) return;
    setActionBusy(true);
    try {
      const activating = toggleTarget.access_status === "INACTIVE";
      await api.post(`/users/${toggleTarget.id}/toggle-active`);
      toast.success(activating ? "Acesso reativado." : "Acesso inativado.");
      setToggleTarget(null);
      await load({ silent: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setActionBusy(false);
    }
  };

  const confirmRemove = async () => {
    if (!removeTarget) return;
    setActionBusy(true);
    try {
      await api.post(`/users/${removeTarget.id}/remove`);
      toast.success("Usuário removido da operação. Histórico preservado.");
      setRemoveTarget(null);
      await load({ silent: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setActionBusy(false);
    }
  };

  const handleCreated = async () => load({ silent: true });
  const handleUpdated = async () => load({ silent: true });

  const roleOptions = tab === "students"
    ? [{ value: "STUDENT", label: "Aluno" }]
    : [
        { value: "ALL", label: "Todos os perfis" },
        { value: "MASTER", label: "Master" },
        { value: "ADMIN", label: "Admin" },
        { value: "PROFESSOR", label: "Professor" },
        ...(tab === "removed" ? [{ value: "STUDENT", label: "Aluno" }] : []),
      ];

  const emptyCopy = tab === "staff"
    ? { icon: UserCog, title: "Nenhuma conta da equipe encontrada", description: "Ajuste os filtros ou crie um novo usuário da equipe." }
    : tab === "students"
      ? { icon: GraduationCap, title: "Nenhuma conta de aluno encontrada", description: "Ajuste os filtros. A vida acadêmica continua sendo administrada em Gestão > Alunos." }
      : { icon: Archive, title: "Nenhum usuário removido", description: "Contas removidas logicamente aparecem aqui com o histórico preservado." };

  return (
    <div className="mx-auto max-w-7xl space-y-4 p-4 sm:p-5 md:p-6" data-testid="users-page">
      <PageHeader
        compact
        eyebrow="Gestão"
        title="Usuários"
        description="Central de contas e acessos do Doxa School. Perfis definem o papel; estados de acesso dizem quem consegue entrar."
        meta={`${staffUsers.length} conta(s) de equipe · ${studentUsers.length} conta(s) de aluno · ${removedUsers.length} removida(s)`}
        action={(
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="doxa-btn-primary inline-flex items-center gap-2 px-4 py-2.5"
            data-testid="new-user-btn"
          >
            <Plus className="h-4 w-4" /> Novo usuário da equipe
          </button>
        )}
      />

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4" aria-label="Resumo de contas">
        <StatCard
          compact
          title="Equipe ativa"
          value={stats.staffActive}
          hint="Contas de gestão e professores com login liberado."
          icon={UserCheck}
          tone="success"
          onClick={() => { switchTab("staff"); setAccessFilter("ACTIVE"); }}
          showActionLabel={false}
          testId="users-stat-staff-active"
        />
        <StatCard
          compact
          title="Acesso pendente"
          value={stats.pendingAccess}
          hint="Primeiro acesso ou redefinição ainda não concluídos."
          icon={KeyRound}
          tone="warning"
          onClick={() => { switchTab("staff"); setAccessFilter("PENDING"); }}
          showActionLabel={false}
          testId="users-stat-pending"
        />
        <StatCard
          compact
          title="Equipe inativa"
          value={stats.inactive}
          hint="Contas preservadas, mas temporariamente sem login."
          icon={UserRoundX}
          tone="neutral"
          onClick={() => { switchTab("staff"); setAccessFilter("INACTIVE"); }}
          showActionLabel={false}
          testId="users-stat-inactive"
        />
        <StatCard
          compact
          title="Removidos"
          value={stats.removed}
          hint="Fora da operação, com IDs e histórico preservados."
          icon={Archive}
          tone="critical"
          onClick={() => switchTab("removed")}
          showActionLabel={false}
          testId="users-stat-removed"
        />
      </div>

      <section className="rounded-2xl border border-[#E0D9CD] bg-white p-3 sm:p-4" aria-label="Visões de usuários">
        <div className="inline-flex w-full rounded-xl border border-[#E0D9CD] bg-[#FAF7F1] p-1 sm:w-auto">
          {TAB_OPTIONS.map(({ value, label, icon: Icon }) => {
            const count = value === "staff" ? staffUsers.length : value === "students" ? studentUsers.length : removedUsers.length;
            const active = tab === value;
            return (
              <button
                key={value}
                type="button"
                onClick={() => switchTab(value)}
                className={`inline-flex flex-1 items-center justify-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold transition sm:flex-none ${active ? "bg-white text-[#D94A12] shadow-sm" : "text-[#6B655E] hover:text-[#1F1F1F]"}`}
                data-testid={`users-tab-${value}`}
              >
                <Icon className="h-4 w-4" /> {label}
                <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold leading-none ${active ? "bg-[#FFF0E9] text-[#D94A12]" : "bg-[#EEE8DF] text-[#6B655E]"}`}>{count}</span>
              </button>
            );
          })}
        </div>
        {tab === "students" && (
          <div className="mt-3 flex flex-col gap-2 rounded-xl border border-[#F3DDCF] bg-[#FFF8F4] px-3.5 py-3 text-xs leading-relaxed text-[#6B655E] sm:flex-row sm:items-center sm:justify-between">
            <span><strong className="text-[#4A4640]">Conta de acesso ≠ situação acadêmica.</strong> Aqui você controla login; turma, presença e jornada permanecem em Alunos.</span>
            <button type="button" onClick={() => navigate("/students")} className="inline-flex shrink-0 items-center gap-1 font-semibold text-[#D94A12] hover:text-[#B83D0D]">
              Abrir Alunos <ExternalLink className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </section>

      <SectionCard
        compact
        title="Encontrar contas"
        description="Busque por pessoa ou e-mail e refine a lista pelo contexto de acesso."
        icon={Search}
        testId="users-filters"
      >
        <div className="flex flex-col gap-3">
          <div className="flex h-11 items-center gap-2 rounded-xl border border-[#E0D9CD] bg-[#FBF9F5] px-3 transition focus-within:border-[#F75A1B] focus-within:bg-white focus-within:ring-2 focus-within:ring-[#F75A1B]/10">
            <Search className="h-4 w-4 shrink-0 text-[#8A8378]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por nome, e-mail, perfil, turma ou estado…"
              className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-[#A59C90]"
              data-testid="users-search"
            />
            {q && (
              <button type="button" onClick={() => setQ("")} className="rounded-md p-1 text-[#8A8378] hover:bg-[#F3EFE7] hover:text-[#1F1F1F]" aria-label="Limpar busca">
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {tab !== "students" && (
              <DoxaSelect
                value={roleFilter}
                onValueChange={setRoleFilter}
                options={roleOptions}
                size="compact"
                testId="users-filter-role"
                ariaLabel="Filtrar por perfil"
              />
            )}
            {tab !== "removed" && (
              <DoxaSelect
                value={accessFilter}
                onValueChange={setAccessFilter}
                options={ACCESS_FILTER_OPTIONS}
                size="compact"
                testId="users-filter-access"
                ariaLabel="Filtrar por acesso"
              />
            )}
            <DoxaSelect
              value={sortBy}
              onValueChange={setSortBy}
              options={SORT_OPTIONS}
              size="compact"
              testId="users-sort"
              ariaLabel="Ordenar usuários"
            />
            <button
              type="button"
              onClick={clearFilters}
              className="h-9 rounded-xl px-3 text-xs font-semibold text-[#6B655E] transition hover:bg-[#FFF0E9] hover:text-[#D94A12]"
              data-testid="users-clear-filters"
            >
              Limpar filtros
            </button>
            <span className="ml-auto text-xs font-medium text-[#8A8378]">{filtered.length} resultado(s)</span>
          </div>
        </div>
      </SectionCard>

      <section className="overflow-hidden rounded-2xl border border-[#E0D9CD] bg-white" aria-label="Lista de usuários">
        <div className="hidden overflow-x-auto lg:block">
          <table className="w-full text-sm">
            <thead className="border-b border-[#E8E0D5] bg-[#FAF7F1] text-left text-xs font-semibold uppercase tracking-[0.05em] text-[#8A8378]">
              <tr>
                <th className="px-4 py-3">Pessoa</th>
                <th className="px-4 py-3">Perfil</th>
                <th className="px-4 py-3">Acesso</th>
                <th className="px-4 py-3">Último acesso</th>
                {tab === "students" && <th className="px-4 py-3">Acadêmico</th>}
                <th className="w-16 px-4 py-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#EEE8DF]">
              {filtered.map((account) => (
                <tr key={account.id} className="transition hover:bg-[#FFFCF8]" data-testid={`user-row-${account.id}`}>
                  <td className="px-4 py-3.5">
                    <button type="button" onClick={() => setDetailsId(account.id)} className="flex min-w-0 items-center gap-3 text-left group">
                      <AccountAvatar user={account} />
                      <span className="min-w-0">
                        <span className="flex items-center gap-2">
                          <span className="truncate font-semibold text-[#1F1F1F] group-hover:text-[#D94A12]">{account.name}</span>
                          {account.is_self && <span className="rounded-full bg-[#FFF0E9] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#D94A12]">Você</span>}
                        </span>
                        <span className="mt-0.5 block truncate text-xs text-[#8A8378]">{account.email}</span>
                      </span>
                    </button>
                  </td>
                  <td className="px-4 py-3.5"><RoleBadge role={account.role} /></td>
                  <td className="max-w-[230px] px-4 py-3.5"><AccessBadge user={account} withDescription /></td>
                  <td className="px-4 py-3.5">
                    <p className={`font-medium ${account.last_login_at ? "text-[#4A4640]" : "text-[#8A8378]"}`}>{relativeLogin(account.last_login_at)}</p>
                    {account.last_login_at && <p className="mt-0.5 text-[11px] text-[#9A9287]">{brDateTime(account.last_login_at)}</p>}
                  </td>
                  {tab === "students" && (
                    <td className="px-4 py-3.5">
                      {account.student ? (
                        <div className="space-y-1">
                          <span className={`doxa-badge ${ACADEMIC_BADGE[account.student.status] || "doxa-badge-neutral"}`}>{tr(STUDENT_STATUS_LABEL, account.student.status)}</span>
                          <p className="max-w-[180px] truncate text-[11px] text-[#8A8378]">{account.student.class_group_name || "Sem turma atual"}</p>
                        </div>
                      ) : <span className="text-xs text-[#8A8378]">Sem vínculo acadêmico</span>}
                    </td>
                  )}
                  <td className="px-4 py-3.5 text-right">
                    <AccountActions
                      account={account}
                      onDetails={(item) => setDetailsId(item.id)}
                      onEdit={setEditTarget}
                      onReset={setResetTarget}
                      onToggle={setToggleTarget}
                      onRemove={setRemoveTarget}
                      onOpenStudent={openStudent}
                      onOpenRegistration={openRegistration}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="space-y-3 p-3 lg:hidden" data-testid="users-cards-mobile">
          {filtered.map((account) => (
            <article key={account.id} className="rounded-2xl border border-[#E7E0D5] bg-white p-4" data-testid={`user-card-${account.id}`}>
              <div className="flex items-start gap-3">
                <button type="button" onClick={() => setDetailsId(account.id)} className="flex min-w-0 flex-1 items-start gap-3 text-left">
                  <AccountAvatar user={account} />
                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-1.5">
                      <span className="font-semibold text-[#1F1F1F]">{account.name}</span>
                      {account.is_self && <span className="rounded-full bg-[#FFF0E9] px-1.5 py-0.5 text-[9px] font-bold uppercase text-[#D94A12]">Você</span>}
                    </span>
                    <span className="mt-0.5 block break-all text-xs text-[#8A8378]">{account.email}</span>
                  </span>
                </button>
                <AccountActions
                  mobile
                  account={account}
                  onDetails={(item) => setDetailsId(item.id)}
                  onEdit={setEditTarget}
                  onReset={setResetTarget}
                  onToggle={setToggleTarget}
                  onRemove={setRemoveTarget}
                  onOpenStudent={openStudent}
                  onOpenRegistration={openRegistration}
                />
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-2">
                <RoleBadge role={account.role} />
                <AccessBadge user={account} />
                {account.student && <span className={`doxa-badge ${ACADEMIC_BADGE[account.student.status] || "doxa-badge-neutral"}`}>Acadêmico: {tr(STUDENT_STATUS_LABEL, account.student.status)}</span>}
              </div>

              <div className="mt-3 grid gap-2 border-t border-[#EEE8DF] pt-3 text-xs text-[#6B655E] sm:grid-cols-2">
                <span className="inline-flex items-center gap-1.5"><Clock3 className="h-3.5 w-3.5 text-[#F75A1B]" /> {relativeLogin(account.last_login_at)}</span>
                {account.student?.class_group_name && <span className="inline-flex items-center gap-1.5"><GraduationCap className="h-3.5 w-3.5 text-[#8A8378]" /> {account.student.class_group_name}</span>}
              </div>
            </article>
          ))}
        </div>

        {!loading && filtered.length === 0 && (
          <EmptyState icon={emptyCopy.icon} title={emptyCopy.title} description={emptyCopy.description} />
        )}
        {loading && (
          <div className="flex items-center justify-center gap-2 py-12 text-sm text-[#8A8378]">
            <Loader2 className="h-4 w-4 animate-spin text-[#F75A1B]" /> Carregando contas…
          </div>
        )}
      </section>

      <AccountDetailsSheet
        account={selectedAccount}
        open={!!selectedAccount}
        onOpenChange={(open) => { if (!open) setDetailsId(null); }}
        onEdit={setEditTarget}
        onReset={setResetTarget}
        onToggle={setToggleTarget}
        onRemove={setRemoveTarget}
        onOpenStudent={openStudent}
        onOpenRegistration={openRegistration}
      />

      <CreateUserDialog
        open={showCreate}
        onOpenChange={setShowCreate}
        currentRole={currentUser?.role}
        onCreated={handleCreated}
        onInvite={(payload) => setInviteResult({ kind: "created", ...payload })}
      />

      <EditUserDialog
        account={editTarget}
        open={!!editTarget}
        onOpenChange={(open) => { if (!open) setEditTarget(null); }}
        currentRole={currentUser?.role}
        onUpdated={handleUpdated}
      />

      <AlertDialog open={!!resetTarget} onOpenChange={(open) => { if (!open && !actionBusy) setResetTarget(null); }}>
        <AlertDialogContent data-testid="reset-user-dialog" className="rounded-2xl border-[#E0D9CD]">
          <AlertDialogHeader>
            <AlertDialogTitle>{["FIRST_ACCESS_PENDING", "RESET_PENDING"].includes(resetTarget?.access_context) ? "Gerar novo link de acesso?" : "Redefinir acesso?"}</AlertDialogTitle>
            <AlertDialogDescription className="leading-relaxed">
              {resetTarget ? (
                resetTarget.access_status === "ACTIVE"
                  ? `A senha atual de ${resetTarget.name} deixará de funcionar. Um novo link será gerado e a conta ficará pendente até a pessoa definir uma nova senha.`
                  : `Um novo link de primeiro acesso será gerado para ${resetTarget.name}. O link anterior deixa de ser o caminho indicado para concluir o acesso.`
              ) : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionBusy}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); void confirmReset(); }} disabled={actionBusy} className="bg-[#F75A1B] hover:bg-[#D94A12]" data-testid="reset-user-confirm">
              {actionBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} Gerar novo acesso
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!toggleTarget} onOpenChange={(open) => { if (!open && !actionBusy) setToggleTarget(null); }}>
        <AlertDialogContent data-testid="toggle-user-dialog" className="rounded-2xl border-[#E0D9CD]">
          <AlertDialogHeader>
            <AlertDialogTitle>{toggleTarget?.access_status === "ACTIVE" ? "Inativar acesso?" : "Reativar acesso?"}</AlertDialogTitle>
            <AlertDialogDescription className="leading-relaxed">
              {toggleTarget?.access_status === "ACTIVE"
                ? `${toggleTarget?.name || "Este usuário"} deixará de conseguir entrar no Doxa School. O cadastro, o perfil e todo o histórico permanecem preservados.`
                : `${toggleTarget?.name || "Este usuário"} voltará a conseguir entrar com a senha já definida. Esta ação altera somente o acesso à conta.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionBusy}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); void confirmToggle(); }} disabled={actionBusy} className="bg-[#F75A1B] hover:bg-[#D94A12]" data-testid="toggle-user-confirm">
              {actionBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Power className="h-4 w-4" />} {toggleTarget?.access_status === "ACTIVE" ? "Inativar acesso" : "Reativar acesso"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!removeTarget} onOpenChange={(open) => { if (!open && !actionBusy) setRemoveTarget(null); }}>
        <AlertDialogContent data-testid="remove-user-dialog" className="rounded-2xl border-[#E0D9CD]">
          <AlertDialogHeader>
            <AlertDialogTitle>Remover da operação?</AlertDialogTitle>
            <AlertDialogDescription className="leading-relaxed">
              {removeTarget ? `${removeTarget.name} será retirado(a) das listas operacionais e não poderá mais acessar o Doxa School. A conta não será apagada: ID, histórico, provas, notas e demais registros permanecem preservados.` : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionBusy} data-testid="remove-user-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); void confirmRemove(); }} disabled={actionBusy} className="bg-[#C53030] hover:bg-[#A52626]" data-testid="remove-user-confirm">
              {actionBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Remover da operação
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <InviteResultDialog result={inviteResult} onOpenChange={(open) => { if (!open) setInviteResult(null); }} />
    </div>
  );
}

function AccountDetailsSheet({ account, open, onOpenChange, onEdit, onReset, onToggle, onRemove, onOpenStudent, onOpenRegistration }) {
  if (!account) return null;
  const role = ROLE_META[account.role] || ROLE_META.STUDENT;
  const access = accessMeta(account);
  const caps = account.capabilities || {};
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full overflow-y-auto border-[#E0D9CD] bg-[#FBF9F5] p-0 sm:max-w-lg">
        <div className="border-b border-[#E0D9CD] bg-white px-5 py-6 pr-12">
          <SheetHeader>
            <div className="flex items-start gap-3">
              <AccountAvatar user={account} size="lg" />
              <div className="min-w-0">
                <SheetTitle className="font-display text-xl font-bold text-[#1F1F1F]">{account.name}</SheetTitle>
                <SheetDescription className="mt-1 break-all text-[#6B655E]">{account.email}</SheetDescription>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <RoleBadge role={account.role} />
                  <AccessBadge user={account} />
                  {account.is_self && <span className="rounded-full bg-[#FFF0E9] px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-[#D94A12]">Você</span>}
                </div>
              </div>
            </div>
          </SheetHeader>
        </div>

        <div className="space-y-4 p-4 sm:p-5">
          <section className="rounded-2xl border border-[#E0D9CD] bg-white p-4">
            <div className="flex items-center gap-2 text-[#F75A1B]"><ShieldCheck className="h-4 w-4" /><h3 className="font-semibold text-[#1F1F1F]">Conta</h3></div>
            <dl className="mt-3 divide-y divide-[#EEE8DF] text-sm">
              <DetailRow label="Perfil" value={role.label} hint={role.description} />
              <DetailRow label="Criado em" value={brDateTime(account.created_at)} />
              <DetailRow label="Privacidade" value={account.privacy_notice_accepted_at ? "Aceita" : "Ainda não aceita"} />
            </dl>
          </section>

          <section className="rounded-2xl border border-[#E0D9CD] bg-white p-4">
            <div className="flex items-center gap-2 text-[#F75A1B]"><KeyRound className="h-4 w-4" /><h3 className="font-semibold text-[#1F1F1F]">Acesso</h3></div>
            <div className="mt-3 rounded-xl bg-[#FAF7F1] p-3">
              <AccessBadge user={account} />
              <p className="mt-2 text-xs leading-relaxed text-[#6B655E]">{access.description}</p>
            </div>
            <dl className="mt-3 divide-y divide-[#EEE8DF] text-sm">
              <DetailRow label="Último acesso" value={account.last_login_at ? brDateTime(account.last_login_at) : "Nunca acessou"} />
              <DetailRow label="Primeiro acesso" value={account.has_pending_invite ? "Há um acesso pendente" : "Sem convite pendente"} />
              {account.removed_at && <DetailRow label="Removido em" value={brDateTime(account.removed_at)} />}
            </dl>
          </section>

          {account.student && (
            <section className="rounded-2xl border border-[#E0D9CD] bg-white p-4">
              <div className="flex items-center gap-2 text-[#F75A1B]"><GraduationCap className="h-4 w-4" /><h3 className="font-semibold text-[#1F1F1F]">Vínculo acadêmico</h3></div>
              <p className="mt-2 text-xs leading-relaxed text-[#6B655E]">A conta controla login. A situação acadêmica é independente e permanece em Gestão &gt; Alunos.</p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className={`doxa-badge ${ACADEMIC_BADGE[account.student.status] || "doxa-badge-neutral"}`}>{tr(STUDENT_STATUS_LABEL, account.student.status)}</span>
                {account.student.class_group_name && <span className="text-xs font-medium text-[#6B655E]">{account.student.class_group_name}</span>}
              </div>
              <button type="button" onClick={() => onOpenStudent(account)} className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-[#D94A12] hover:text-[#B83D0D]">
                Abrir perfil acadêmico <ExternalLink className="h-3.5 w-3.5" />
              </button>
            </section>
          )}

          <section className="rounded-2xl border border-[#E0D9CD] bg-white p-4">
            <h3 className="font-semibold text-[#1F1F1F]">Ações disponíveis</h3>
            <p className="mt-1 text-xs text-[#8A8378]">As ações abaixo já consideram seu perfil, o alvo e o estado atual da conta.</p>
            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              {caps.edit && <ActionButton icon={Pencil} label="Editar usuário" onClick={() => onEdit(account)} />}
              {caps.reset_access && <ActionButton icon={KeyRound} label={["FIRST_ACCESS_PENDING", "RESET_PENDING"].includes(account.access_context) ? "Gerar novo link" : "Redefinir acesso"} onClick={() => onReset(account)} />}
              {caps.toggle_access && <ActionButton icon={Power} label={account.access_status === "ACTIVE" ? "Inativar acesso" : "Reativar acesso"} onClick={() => onToggle(account)} />}
              {account.access_context === "REGISTRATION_PENDING" && <ActionButton icon={ExternalLink} label="Abrir em Inscrições" onClick={() => onOpenRegistration(account)} />}
              {caps.remove && <ActionButton danger icon={Trash2} label="Remover da operação" onClick={() => onRemove(account)} />}
              {!caps.edit && !caps.reset_access && !caps.toggle_access && !caps.remove && account.access_context !== "REGISTRATION_PENDING" && (
                <p className="col-span-full rounded-xl bg-[#FAF7F1] px-3 py-3 text-xs leading-relaxed text-[#6B655E]">Não há ação administrativa disponível para esta conta no estado atual.</p>
              )}
            </div>
          </section>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function DetailRow({ label, value, hint }) {
  return (
    <div className="grid grid-cols-[110px_1fr] gap-3 py-2.5 first:pt-0 last:pb-0">
      <dt className="text-xs font-medium text-[#8A8378]">{label}</dt>
      <dd className="text-right text-sm font-medium text-[#4A4640]">
        {value || "—"}
        {hint && <span className="mt-1 block text-[11px] font-normal leading-relaxed text-[#9A9287]">{hint}</span>}
      </dd>
    </div>
  );
}

function ActionButton({ icon: Icon, label, onClick, danger = false }) {
  return (
    <button type="button" onClick={onClick} className={`inline-flex min-h-10 items-center justify-center gap-2 rounded-xl border px-3 py-2 text-xs font-semibold transition ${danger ? "border-[#F2CACA] bg-[#FFF8F8] text-[#C53030] hover:bg-[#FDECEC]" : "border-[#E0D9CD] bg-white text-[#4A4640] hover:border-[#F75A1B]/35 hover:bg-[#FFF7F2] hover:text-[#D94A12]"}`}>
      <Icon className="h-4 w-4" /> {label}
    </button>
  );
}

function CreateUserDialog({ open, onOpenChange, currentRole, onCreated, onInvite }) {
  const allowedRoles = currentRole === "MASTER" ? ["MASTER", "ADMIN", "PROFESSOR"] : ["PROFESSOR"];
  const [form, setForm] = useState({ name: "", email: "", role: allowedRoles[0] || "PROFESSOR", password: "" });
  const [accessMode, setAccessMode] = useState("invite");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({ name: "", email: "", role: allowedRoles[0] || "PROFESSOR", password: "" });
      setAccessMode("invite");
    }
  }, [open, currentRole]);

  const roleOptions = allowedRoles.map((role) => ({ value: role, label: ROLE_META[role]?.label || role }));
  const roleMeta = ROLE_META[form.role] || ROLE_META.PROFESSOR;

  const submit = async (event) => {
    event.preventDefault();
    if (!form.name.trim()) return toast.error("Informe o nome do usuário.");
    if (!form.email.trim()) return toast.error("Informe o e-mail do usuário.");
    if (accessMode === "password" && form.password.length < 6) return toast.error("A senha precisa ter pelo menos 6 caracteres.");
    setBusy(true);
    try {
      const payload = { name: form.name.trim(), email: form.email.trim(), role: form.role };
      if (accessMode === "password") payload.password = form.password;
      const { data } = await api.post("/users", payload);
      await onCreated();
      onOpenChange(false);
      if (data.invite_token) {
        onInvite({ name: form.name.trim(), url: `${window.location.origin}/first-access/${data.invite_token}` });
      } else {
        toast.success("Usuário criado com acesso ativo.");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!busy) onOpenChange(next); }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-2xl border-[#E0D9CD] sm:max-w-2xl" data-testid="new-user-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Novo usuário da equipe</DialogTitle>
          <DialogDescription>Crie a identidade, escolha o perfil e defina como a pessoa fará o primeiro acesso.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="space-y-1.5">
              <span className="text-xs font-semibold text-[#6B655E]">Nome completo</span>
              <input required value={form.name} onChange={(e) => setForm((current) => ({ ...current, name: e.target.value }))} className={INPUT_CLASS} placeholder="Ex.: Paulo Costa" data-testid="new-user-name" />
            </label>
            <label className="space-y-1.5">
              <span className="text-xs font-semibold text-[#6B655E]">E-mail de acesso</span>
              <input required type="email" value={form.email} onChange={(e) => setForm((current) => ({ ...current, email: e.target.value }))} className={INPUT_CLASS} placeholder="nome@email.com" data-testid="new-user-email" />
            </label>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between gap-3">
              <span className="text-xs font-semibold text-[#6B655E]">Perfil no sistema</span>
              <span className="text-[11px] text-[#9A9287]">Permissões continuam definidas em Sistema &gt; Permissões</span>
            </div>
            <DoxaSelect value={form.role} onValueChange={(role) => setForm((current) => ({ ...current, role }))} options={roleOptions} className="w-full" testId="new-user-role" ariaLabel="Perfil do novo usuário" disabled={roleOptions.length === 1} />
            <div className="mt-2 rounded-xl bg-[#FAF7F1] px-3 py-2.5 text-xs leading-relaxed text-[#6B655E]">
              <strong className="text-[#4A4640]">{roleMeta.label}:</strong> {roleMeta.description}
              {form.role === "MASTER" && <span className="mt-1 block font-semibold text-[#C05621]">Master possui acesso irrestrito ao Doxa School.</span>}
            </div>
          </div>

          <div>
            <span className="text-xs font-semibold text-[#6B655E]">Como será o primeiro acesso?</span>
            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              <button type="button" onClick={() => setAccessMode("invite")} className={`rounded-2xl border p-3 text-left transition ${accessMode === "invite" ? "border-[#F75A1B] bg-[#FFF7F2] ring-2 ring-[#F75A1B]/10" : "border-[#E0D9CD] bg-white hover:bg-[#FAF7F1]"}`}>
                <span className="flex items-center gap-2 font-semibold text-[#1F1F1F]"><KeyRound className="h-4 w-4 text-[#F75A1B]" /> Link de primeiro acesso</span>
                <span className="mt-1 block text-xs leading-relaxed text-[#6B655E]">Recomendado. A pessoa cria a própria senha e aceita o aviso de privacidade.</span>
                <span className="mt-2 inline-flex rounded-full bg-[#E6F4EA] px-2 py-0.5 text-[10px] font-bold text-[#2F855A]">Recomendado</span>
              </button>
              <button type="button" onClick={() => setAccessMode("password")} className={`rounded-2xl border p-3 text-left transition ${accessMode === "password" ? "border-[#F75A1B] bg-[#FFF7F2] ring-2 ring-[#F75A1B]/10" : "border-[#E0D9CD] bg-white hover:bg-[#FAF7F1]"}`}>
                <span className="flex items-center gap-2 font-semibold text-[#1F1F1F]"><ShieldCheck className="h-4 w-4 text-[#8A8378]" /> Definir senha agora</span>
                <span className="mt-1 block text-xs leading-relaxed text-[#6B655E]">Opção secundária. A conta nasce ativa com a senha definida pela gestão.</span>
              </button>
            </div>
            {accessMode === "password" && (
              <label className="mt-3 block space-y-1.5">
                <span className="text-xs font-semibold text-[#6B655E]">Senha inicial</span>
                <input type="password" value={form.password} onChange={(e) => setForm((current) => ({ ...current, password: e.target.value }))} className={INPUT_CLASS} placeholder="Mínimo de 6 caracteres" autoComplete="new-password" />
              </label>
            )}
          </div>

          <DialogFooter>
            <button type="button" onClick={() => onOpenChange(false)} disabled={busy} className="rounded-xl border border-[#E0D9CD] px-4 py-2.5 text-sm font-semibold text-[#6B655E] hover:bg-[#F8F3EA] disabled:opacity-50">Cancelar</button>
            <button type="submit" disabled={busy} className="doxa-btn-primary inline-flex items-center justify-center gap-2 px-4 py-2.5" data-testid="new-user-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Criar usuário
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditUserDialog({ account, open, onOpenChange, currentRole, onUpdated }) {
  const [form, setForm] = useState({ name: "", email: "", role: "PROFESSOR" });
  const [busy, setBusy] = useState(false);
  const allowedRoles = currentRole === "MASTER" ? ["MASTER", "ADMIN", "PROFESSOR"] : ["PROFESSOR"];

  useEffect(() => {
    if (account && open) setForm({ name: account.name || "", email: account.email || "", role: account.role || "PROFESSOR" });
  }, [account, open]);

  if (!account) return null;
  const roleOptions = allowedRoles.map((role) => ({ value: role, label: ROLE_META[role]?.label || role }));

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      await api.put(`/users/${account.id}`, { name: form.name.trim(), email: form.email.trim(), role: form.role });
      await onUpdated();
      toast.success("Usuário atualizado.");
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!busy) onOpenChange(next); }}>
      <DialogContent className="rounded-2xl border-[#E0D9CD] sm:max-w-lg" data-testid="edit-user-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Editar usuário</DialogTitle>
          <DialogDescription>Altere a identidade da conta e o perfil herdado. Permissões individuais não são configuradas aqui.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <label className="block space-y-1.5">
            <span className="text-xs font-semibold text-[#6B655E]">Nome</span>
            <input required value={form.name} onChange={(e) => setForm((current) => ({ ...current, name: e.target.value }))} className={INPUT_CLASS} data-testid="edit-user-name" />
          </label>
          <label className="block space-y-1.5">
            <span className="text-xs font-semibold text-[#6B655E]">E-mail de login</span>
            <input required type="email" value={form.email} onChange={(e) => setForm((current) => ({ ...current, email: e.target.value }))} className={INPUT_CLASS} data-testid="edit-user-email" />
            {form.email !== account.email && <span className="block text-[11px] text-[#C05621]">O novo e-mail passará a ser usado para login.</span>}
          </label>
          <div className="space-y-1.5">
            <span className="text-xs font-semibold text-[#6B655E]">Perfil</span>
            <DoxaSelect value={form.role} onValueChange={(role) => setForm((current) => ({ ...current, role }))} options={roleOptions} className="w-full" testId="edit-user-role" ariaLabel="Perfil do usuário" />
            {form.role !== account.role && <span className="block text-[11px] text-[#C05621]">A pessoa passará a herdar as permissões configuradas para o novo perfil.</span>}
            {form.role === "MASTER" && <span className="block text-[11px] font-semibold text-[#C05621]">Master possui acesso irrestrito ao Doxa School.</span>}
          </div>
          <DialogFooter>
            <button type="button" onClick={() => onOpenChange(false)} disabled={busy} className="rounded-xl border border-[#E0D9CD] px-4 py-2.5 text-sm font-semibold text-[#6B655E] hover:bg-[#F8F3EA] disabled:opacity-50">Cancelar</button>
            <button type="submit" disabled={busy} className="doxa-btn-primary inline-flex items-center justify-center gap-2 px-4 py-2.5" data-testid="edit-user-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Salvar alterações
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function InviteResultDialog({ result, onOpenChange }) {
  const [copied, setCopied] = useState("");
  useEffect(() => { if (result) setCopied(""); }, [result]);
  if (!result) return null;

  const message = `Olá, ${result.name}! Seu acesso ao Doxa School está pronto. Use este link para definir sua senha e concluir o primeiro acesso: ${result.url}`;
  const copy = async (value, type) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(type);
      toast.success(type === "link" ? "Link copiado." : "Mensagem copiada.");
    } catch {
      toast.error("Não foi possível copiar automaticamente. Selecione o texto e copie manualmente.");
    }
  };

  return (
    <Dialog open={!!result} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl border-[#E0D9CD] sm:max-w-lg" data-testid="invite-result-dialog">
        <DialogHeader>
          <div className="mb-1 grid h-11 w-11 place-items-center rounded-xl bg-[#E6F4EA] text-[#2F855A]"><Check className="h-5 w-5" /></div>
          <DialogTitle className="font-display text-xl">{result.kind === "created" ? "Usuário criado" : "Acesso redefinido"}</DialogTitle>
          <DialogDescription>{result.name} precisa usar este link para definir a própria senha e concluir o acesso.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-xl border border-[#E0D9CD] bg-[#FAF7F1] p-3">
            <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#8A8378]">Link de primeiro acesso</p>
            <p className="mt-2 break-all text-xs font-medium text-[#4A4640]" data-testid="invite-result-url">{result.url}</p>
            <button type="button" onClick={() => copy(result.url, "link")} className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-2 text-xs font-semibold text-[#D94A12] ring-1 ring-[#E0D9CD] hover:bg-[#FFF7F2]">
              {copied === "link" ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />} {copied === "link" ? "Copiado" : "Copiar link"}
            </button>
          </div>
          <button type="button" onClick={() => copy(message, "message")} className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-[#E0D9CD] bg-white px-4 py-2.5 text-sm font-semibold text-[#4A4640] hover:bg-[#FAF7F1]">
            {copied === "message" ? <Check className="h-4 w-4 text-[#2F855A]" /> : <Mail className="h-4 w-4 text-[#F75A1B]" />} {copied === "message" ? "Mensagem copiada" : "Copiar mensagem de convite"}
          </button>
          <p className="text-[11px] leading-relaxed text-[#8A8378]">O link só aparece nesta confirmação. A lista de usuários não expõe o token do convite.</p>
        </div>
        <DialogFooter>
          <button type="button" onClick={() => onOpenChange(false)} className="doxa-btn-primary px-4 py-2.5">Concluir</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}